import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "./utils/api";
import { isValidDateString } from "./utils/utils";

interface Position {
  top: number;
  left: number;
}

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function todayDateString(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Global "select text anywhere, press F, create an upcoming calendar event" widget. Mounted
// once at the app root (see App.tsx) so it works regardless of which page/dockview panel is
// active. The marked text is appended to the note title, and the whole thing is created
// directly as a new event in the Upcoming calendar (POST /calendar-events).
export const MarkedTextNoteWidget = () => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const [highlightRects, setHighlightRects] = useState<HighlightRect[]>([]);
  const [selectedText, setSelectedText] = useState("");
  const [tickers, setTickers] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(todayDateString);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const tickersInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "f") return;
      // don't hijack Ctrl/Cmd+F (browser find) or Alt combos
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // don't hijack normal typing in inputs/textareas/contenteditable
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || active?.isContentEditable) return;

      const selection = window.getSelection();
      const text = selection?.toString().trim() ?? "";
      if (!text || !selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      // one rect per visual line of the selection, so a wrapped multi-line selection is
      // rendered as a matching set of boxes instead of one big bounding rectangle
      const rects = Array.from(range.getClientRects())
        .filter((r) => r.width > 0 && r.height > 0)
        .map((r) => ({ top: r.top, left: r.left, width: r.width, height: r.height }));
      if (rects.length === 0) return;

      const boundingRect = range.getBoundingClientRect();

      e.preventDefault();
      setSelectedText(text);
      setHighlightRects(rects);
      setTickers("");
      setTitle("");
      setDate(todayDateString());
      setError("");
      setPosition({
        top: Math.min(boundingRect.bottom + 6, window.innerHeight - 150),
        left: Math.min(Math.max(boundingRect.left, 8), window.innerWidth - 300),
      });
      setVisible(true);
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!visible) return;

    const t = setTimeout(() => tickersInputRef.current?.focus(), 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setVisible(false);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onMouseDown);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onMouseDown);
    };
  }, [visible]);

  function submitNote() {
    if (submitting) return;

    if (!isValidDateString(date)) {
      setError("Date must be in YYYY-MM-DD format");
      return;
    }

    setSubmitting(true);
    setError("");

    const mergedTitle = `${title.trim()} ${selectedText}`.trim();

    api
      .post("/calendar-events", {
        id: -1,
        title: mergedTitle,
        tickers,
        date,
        description: "",
      })
      .then(() => {
        setVisible(false);
      })
      .catch((err) => {
        console.error("Error creating calendar event:", err);
        setError("Failed to save.");
      })
      .finally(() => setSubmitting(false));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submitNote();
  }

  // Enter should submit no matter which field is focused, even though there's no submit
  // button in this compact form (which is what normally drives implicit submission).
  function handleFormKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      submitNote();
    }
  }

  if (!visible) return null;

  return createPortal(
    <>
      {/* Own highlight overlay - independent of the native browser selection, which gets
          cleared as soon as focus moves to the input below, so this keeps the marked text
          visibly highlighted for as long as the popup is open. */}
      {highlightRects.map((r, i) => (
        <div
          key={i}
          className="pointer-events-none fixed z-[9998] bg-blue-500/40"
          style={{ top: r.top, left: r.left, width: r.width, height: r.height }}
        />
      ))}

      <div
        ref={containerRef}
        className="fixed z-[9999] w-72 rounded border border-gray-300 bg-white p-2 shadow-lg dark:border-neutral-600 dark:bg-neutral-900"
        style={{ top: position.top, left: position.left }}
      >
        <p className="mb-1 truncate text-xs text-gray-500" title={selectedText}>
          {selectedText}
        </p>
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="flex flex-col gap-1">
          <input
            ref={tickersInputRef}
            type="text"
            value={tickers}
            onChange={(e) => setTickers(e.target.value.toUpperCase())}
            placeholder="Tickers…"
            className="w-full"
          />
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Note title…"
            className="w-full"
          />
          <input
            type="text"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="YYYY-MM-DD"
            className="w-full"
          />
        </form>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    </>,
    document.body,
  );
};
